import React, { useMemo } from 'react'

import { Box, Icon, Tag, Text } from '@factorialco/deprecated-design-system'
import { EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { RoleLevelsSelectorDocument } from 'generated/resources/graphql'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import { flatSelection } from 'modules/core/components/EntitySelectWrapper/lib/flat'
import LabeledInput from 'modules/core/components/LabeledInput'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ParsedEntity, RoleLevelsSelectorProps } from './types'
import { extractRoleLevelsFromTree, parseRoleLevelsFromExtracted } from './utils'

export const RoleLevelsSelector: React.FC<RoleLevelsSelectorProps> = ({
  onChange,
  selected,
  exclude = [],
}) => {
  const { data, loading } = useQuery(RoleLevelsSelectorDocument)

  const roleLevels = useMemo(() => {
    try {
      const treeData = data?.jobCatalog?.treesConnection?.nodes?.[0]
      if (!treeData?.serializedTree) return []

      const parsed = JSON.parse(treeData.serializedTree)
      const tree = Array.isArray(parsed) ? parsed[0] : parsed
      const levels = extractRoleLevelsFromTree(tree)

      return levels
    } catch {
      return []
    }
  }, [data])

  const selectedRoleLevels = roleLevels.filter((level) => selected.includes(level.uuid))
  const availableRoleLevels = roleLevels.filter((level) => !exclude.includes(level.uuid))

  const entities = parseRoleLevelsFromExtracted(availableRoleLevels)

  const onSelect = (selection: EntitySelectEntity[] | null) => {
    if (!selection) {
      onChange?.([])
      return
    }

    const uuids = flatSelection(selection).map((item) => String(item.subId))
    onChange?.(uuids)
  }

  const onRemove = (removedUuid: string) => {
    const uuids = selected.filter((uuid) => uuid !== removedUuid)
    onChange?.(uuids)
  }

  const renderSelectedTags = (
    <LabeledInput
      data-testid="role-levels-selector-tags"
      label={i18n.t('competencies_v3.role_levels_selector.assign')}
      loading={loading}
    >
      <Box
        flexDirection="row"
        justifyContent="spaceBetween"
        gap="s8"
        padding="s8"
        cursor={loading ? 'default' : 'pointer'}
      >
        {selected.length === 0 ? (
          <Box flexDirection="row" alignItems="center" paddingX="s4">
            <Text size="300" color="grey600">
              {i18n.t('competencies_v3.role_levels_selector.placeholder')}
            </Text>
          </Box>
        ) : (
          <Box flexDirection="row" flexWrap="wrap" gap="s8" flexGrow>
            {selectedRoleLevels.map((level) => (
              <Tag
                key={level.uuid}
                inline
                rightItem={loading ? undefined : 'close'}
                type="viridian"
                onClose={
                  loading
                    ? undefined
                    : (event) => {
                        event.stopPropagation()
                        onRemove(level.uuid)
                      }
                }
              >
                {`${level.roleName} ${level.name}`}
              </Tag>
            ))}
          </Box>
        )}
        <Box alignItems="center" justifyContent="center" padding="s4">
          <Icon.Small icon="caret-down" color="grey600" />
        </Box>
      </Box>
    </LabeledInput>
  )

  const selectedEntities = entities.reduce<ParsedEntity[]>((acc, entity) => {
    const filteredSubItems = entity.subItems?.filter((subItem) =>
      selected.includes(String(subItem.subId))
    )

    if (filteredSubItems?.length) {
      acc.push({ ...entity, subItems: filteredSubItems })
    }

    return acc
  }, [])

  if (!loading && availableRoleLevels.length === 0) {
    return (
      <LabeledInput
        data-testid="role-levels-selector-tags"
        label={i18n.t('competencies_v3.role_levels_selector.assign')}
      >
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          gap="s8"
          padding="s8"
          cursor="default"
        >
          <Text size="300" color="grey600">
            {i18n.t('competencies_v3.role_levels_selector.no_jobs_found')}
          </Text>
        </Box>
      </LabeledInput>
    )
  }

  return (
    <Box data-testid="role-levels-selector">
      <EntitySelectWrapper
        entities={entities}
        groups={[]}
        expandedElements={[]}
        selectedGroup="all"
        selectedGroupedEntities={selectedEntities}
        loading={loading}
        multiple
        hiddenAvatar
        hideLabel
        label={i18n.t('competencies_v3.role_levels_selector.assign')}
        singleSelector={false}
        selectedItemsCopy={i18n.t('competencies_v3.role_levels_selector.jobs_selected')}
        searchPlaceholder={i18n.t('common.search')}
        selectAllLabel={i18n.t('common.select_all')}
        clearLabel={i18n.t('common.clear')}
        selectedLabel={i18n.t('common.selected')}
        notFoundSubtitle={i18n.t('factorial_one.collections.empty_states.no_results.description')}
        notFoundTitle={i18n.t('factorial_one.collections.empty_states.no_results.title')}
        width="s400"
        onSelect={() => {}}
        onSelectMultiple={loading ? () => {} : onSelect}
        onGroupChange={() => {}}
        onSelectExpandedElements={() => {}}
        disabled={loading}
      >
        {renderSelectedTags}
      </EntitySelectWrapper>
    </Box>
  )
}

export default RoleLevelsSelector
